/**
 * Simplified Auth Store - Clean separation of concerns
 *
 * This store only manages minimal authentication state.
 * Business logic is extracted to custom hooks for better testability.
 */

import type { User } from "@/types/auth"
import { create } from "zustand"
import { createJSONStorage, persist } from "zustand/middleware"

interface AuthState {
  // Core state - minimal and focused
  user: User | null
  accessToken: string | null
  refreshToken: string | null
  scopes: string[]
  isAuthenticated: boolean

  // Simple state setters
  setUser: (user: User | null) => void
  setTokens: (accessToken: string | null, refreshToken: string | null) => void
  setScopes: (scopes: string[]) => void
  clearAuth: () => void

  // Permission checking methods (for existing components)
  hasScope: (scope: string) => boolean
  hasAnyScope: (scopes: string[]) => boolean
  hasAllScopes: (scopes: string[]) => boolean
  isAdmin: () => boolean
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      // Initial state
      user: null,
      accessToken: null,
      refreshToken: null,
      scopes: [],
      isAuthenticated: false,

      // Simple actions
      setUser: (user) => {
        set({
          user,
          isAuthenticated: !!user,
        })
      },

      setTokens: (accessToken, refreshToken) => {
        set({
          accessToken,
          refreshToken,
          isAuthenticated: !!accessToken,
        })
      },

      setScopes: (scopes) => {
        set({ scopes })
      },

      clearAuth: () => {
        set({
          user: null,
          accessToken: null,
          refreshToken: null,
          scopes: [],
          isAuthenticated: false,
        })
      },

      // Permission checking methods
      hasScope: (scope) => {
        const currentScopes = get().scopes
        // Check for system:admin wildcard first
        if (currentScopes.includes("system:admin")) {
          return true
        }
        return currentScopes.includes(scope)
      },

      hasAnyScope: (scopes) => {
        const currentScopes = get().scopes
        // Check for system:admin wildcard first
        if (currentScopes.includes("system:admin")) {
          return true
        }
        return scopes.some((scope) => currentScopes.includes(scope))
      },

      hasAllScopes: (scopes) => {
        const currentScopes = get().scopes
        // Check for system:admin wildcard first
        if (currentScopes.includes("system:admin")) {
          return true
        }
        return scopes.every((scope) => currentScopes.includes(scope))
      },

      isAdmin: () => get().scopes.includes("system:admin"),
    }),
    {
      name: "auth-store",
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        // Persist necessary data for session restoration
        user: state.user,
        scopes: state.scopes,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          if (state.accessToken) {
            // Restore authentication state if we have an access token
            state.isAuthenticated = true
          } else {
            // No valid token — clear stale user data and scopes to prevent
            // hooks from firing API calls with no credentials.
            // This fixes the infinite polling loop where persisted scopes
            // cause permission checks to pass while tokens are null.
            state.isAuthenticated = false
            state.user = null
            state.scopes = []
            state.refreshToken = null
          }
        }
      },
    },
  ),
)

export default useAuthStore
